import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import type { Tenant } from '@up2d8/contracts';
import { ArrowLeft } from 'lucide-react';
import { AppTabBar, AppTopBar, cn, type AppTabItem } from '@up2d8/ui';
import { GlobalSearch } from '../../../shared/global-search';
import { useSession } from '../../../shared/session';
import { useTenantFeatures } from '../../../shared/use-tenant-features';
import { visibleItems } from '../../../shared/tab-visibility';
import { useTenantTheme } from '../../../shared/use-tenant-theme';
import { TenantMenu } from '../../../shared/tenant-switcher';
import { DeskHeader } from './desk-header';
import { DeskSidebar, deskIcons } from './desk-sidebar';
import { useHasTeam } from '../use-my-team';
import { useHandoverLines } from '../handover/use-handover';
import { useUnreadConversations } from '../messages-icon';
import { useUnreadNotifications } from '../notification-bell';

/**
 * De schil van de medewerkerszone, in twee indelingen (replica-spec §3).
 *
 * Onder 900 px is dit de app zoals ze was: gebrande kopbalk, één kolom, vaste onderbalk. Daarboven
 * is het het bureauscherm uit de mockup: zijbalk van 240 px, kopbalk van 68 px, inhoud eronder.
 *
 * **Eén DOM, geen JavaScript-breekpunt.** De zijbalk is `hidden desk:flex`, de onderbalk
 * `desk:hidden`, en het omschakelen doet CSS. Met een `useMediaQuery` zou de eerste render altijd
 * de verkeerde indeling tonen — een flits bij elk bezoek — en zou elke test afhankelijk worden van
 * een rendercyclus in plaats van van een schermbreedte.
 *
 * `data-zone="app"` blijft op de wortel staan, in beide indelingen. Die stuurt de AA-kleurenset aan,
 * en contrast is geen eigenschap van een schermbreedte. Wat wél aan de breedte hangt — de
 * 16px-vloer en het raakvlak van 48 px — staat sinds UI-5 in een mediaquery in `base.css`.
 */
export interface DeskShellProps {
  /** De tabs van de mobiele onderbalk; op desktop staat dezelfde navigatie in de zijbalk. */
  tabs: AppTabItem[];
  activeKey: string;
  renderLink: (item: AppTabItem, className: string, children: ReactNode) => ReactNode;
  /** Het berichtenicoon in de mobiele kopbalk. */
  messages?: ReactNode;
  /** De meldingenbel in de mobiele kopbalk. */
  bell?: ReactNode;
  /**
   * Ongelezen gesprekken en meldingen voor de kopbalk op desktop. Leeg gelaten haalt de schil ze
   * zelf op; het berichtenscherm geeft zijn eigen, versere telling mee.
   */
  messageCount?: number | null;
  notificationCount?: number | null;
  /** De rechterkolom; alleen op schermen vanaf 1100 px (spec §6). */
  aside?: ReactNode;
  /**
   * Het scherm vult de hoogte en scrolt zelf niet (berichten-spec §2).
   *
   * Dit is een afwijking van elke andere pagina in de app, en daarom een expliciete vlag en geen
   * gevolg van wat er toevallig in `children` staat. Alleen op desktop: op een telefoon is "de
   * pagina scrolt niet" geen ontwerp maar een val, want dan past de inhoud gewoon niet.
   *
   * De inhoud krijgt de volle breedte en het rooster met de rechterkolom vervalt — een scherm dat
   * zijn eigen panelen meebrengt, heeft er geen aan.
   */
  fill?: boolean;
  /**
   * Een scherm onder Home in de vloermodus (VLR-1): onder 900 px een kopbalk van 56 met terugpijl
   * en schermnaam in plaats van de gebrande kopbalk. De onderbalk blijft, met Home actief.
   */
  subBar?: { title: string; onBack: () => void; backLabel: string };
  /**
   * Tijdens het invullen (VLR-1): onder 900 px een vaste voet in plaats van de onderbalk, zodat je
   * niet per ongeluk wegtikt uit een half ingevulde checklist of aanvraag.
   */
  mobileFooter?: ReactNode;
  /**
   * Welk item van de zijbalk oplicht, als dat een ander is dan dat van de onderbalk. Checklists en
   * Formulieren houden Home actief in de onderbalk, maar laten in de zijbalk Mijn taken oplichten.
   */
  deskActiveKey?: string;
  children: ReactNode;
}

/** Van de sleutel van de onderbalk naar de `href` waarop de zijbalk markeert. */
const DESK_HREF: Record<string, string> = {
  home: '/app',
  documents: '/app/documenten',
  agenda: '/app/agenda',
  messages: '/app/gesprekken',
  colleagues: '/app/collegas',
  team: '/app/team',
  handover: '/app/overdracht',
  checklists: '/app/taken',
  profile: '/app/profile',
};

/** De eerste letters van de bedrijfsnaam, voor de tegel in de wisselaar. */
function initials(name: string): string {
  const woorden = name.trim().split(/\s+/).filter(Boolean);
  const eerste = woorden[0];
  if (!eerste) return '··';
  const tweede = woorden[1];
  if (!tweede) return eerste.slice(0, 2).toUpperCase();
  return (eerste.slice(0, 1) + tweede.slice(0, 1)).toUpperCase();
}

export function DeskShell({
  tabs,
  activeKey,
  renderLink,
  messages,
  bell,
  messageCount,
  notificationCount,
  aside,
  fill = false,
  subBar,
  mobileFooter,
  deskActiveKey,
  children,
}: DeskShellProps) {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();

  const tenant = useQuery({
    queryKey: ['tenant', 'self'],
    queryFn: async () => {
      const { data } = await session.api.GET('/api/v1/tenant');
      return (data as Tenant | undefined) ?? null;
    },
  });

  /*
    De huisstijl van de klant (Extra 1).

    Dezelfde query als op de pagina's, dus dit kost geen extra verkeer. De weergavenaam komt
    hieruit en niet uit `/api/v1/tenant`: die laatste geeft de officiele naam, en "Bakkerij
    Janssens & Zonen NV" hoort niet in een zijbalk van 240 px.
  */
  const theme = useTenantTheme();
  const naam = theme?.displayName || (tenant.data?.name ?? '');
  const { isOn } = useTenantFeatures();
  const { hasTeam } = useHasTeam();
  const handoverLines = useHandoverLines();

  /*
    De tellers in kopbalk en zijbalk komen van de schil zelf, niet van het scherm. Zolang alleen de
    berichtenschermen ze meegaven, verscheen de badge van een nieuw bericht pas als je al in
    Berichten zat - en daar heb je hem net niet meer nodig.
  */
  const unreadConversations = useUnreadConversations();
  const unreadNotifications = useUnreadNotifications();
  const shownMessageCount = messageCount ?? unreadConversations.data?.unread ?? null;
  const shownNotificationCount =
    notificationCount ??
    (session.has('notifications.inbox.self') ? unreadNotifications.data : null);

  /*
    De navigatie van een medewerker, niet die van een beheerder.

    Elk item wijst naar een route die bestaat. "Mededelingen" en "Mijn taken" uit de mockup hebben
    bij ons een andere naam dan in de zijbalk van de mockup — ze wijzen naar de tijdlijn en naar de
    checklists. Een item zonder bestemming hoort er niet te staan; dat is hoe een menu vult met
    dingen die niets doen.
  */
  const main = [
    { label: t('deskNav.home'), href: '/app', icon: deskIcons.home },
    // Dezelfde twee vragen als in de onderbalk, en uit dezelfde functie (Extra 6, punt 1): het
    // recht verdwijnt met de module, de vlag met de keuze van de klant.
    ...visibleItems(
      [
        {
          label: t('appNav.documents'),
          href: '/app/documenten',
          icon: deskIcons.documents,
          policy: 'documents.document.read',
          flag: 'documents.library',
        },
        {
          label: t('deskNav.calendar'),
          href: '/app/agenda',
          icon: deskIcons.calendar,
          policy: 'agenda.item.read',
        },
      ],
      { has: session.has, isOn },
    ).map((item) => ({ label: item.label, href: item.href, icon: item.icon })),
    // "Overdracht" onder Kalender (Stap 39, spec §4), alleen voor wie bij een lijn hoort: een item
    // dat naar "je hoort bij geen lijn" wijst, is een item voor niets.
    ...(isOn('shift_handover.handovers') && (handoverLines.data?.length ?? 0) > 0
      ? [{ label: t('handover.navItem'), href: '/app/overdracht', icon: deskIcons.handover }]
      : []),
    ...(session.has('directory.colleagues.read')
      ? [{ label: t('colleagues.title'), href: '/app/collegas', icon: deskIcons.colleagues }]
      : []),
    // "Mijn team" onder Collega's (Stap 17, spec §3). Het recht is niet genoeg: wie het recht
    // heeft maar niemand leidt, hoort geen item te zien dat naar een leeg scherm wijst.
    ...(hasTeam ? [{ label: t('team.link'), href: '/app/team', icon: deskIcons.colleagues }] : []),
    ...(session.has('messaging.conversation.use')
      ? [
          {
            label: t('messaging.title'),
            href: '/app/gesprekken',
            icon: deskIcons.messages,
            badge: shownMessageCount || null,
          },
        ]
      : []),
  ];

  /*
    "Mijn taken" vervangt "Checklists" in de zijbalk (SCH-6).

    Niet allebei: dat zijn twee items die er allebei uitzien als een takenlijst, en dan is de vraag
    welke je moet hebben. "Mijn taken" is de ruimere van de twee - ze toont ook de checklists - en
    een checklist in die lijst linkt door naar het checklistscherm, dat gewoon blijft bestaan.

    Zonder recht op checklists blijft het item staan: er staan ook berichten, formulieren en
    evenementen in, en elk van die vier kan ontbreken zonder dat het scherm leeg is.
  */
  const personal = [
    { label: t('myTasks.title'), href: '/app/taken', icon: deskIcons.tasks },
    { label: t('deskNav.settings'), href: '/app/profile', icon: deskIcons.settings },
  ];

  const support = import.meta.env.VITE_SUPPORT_URL ?? null;

  return (
    <div data-zone="app" className="flex min-h-screen bg-background">
      <DeskSidebar
        organisation={naam}
        organisationInitials={initials(naam)}
        // Het donkere logo op de donkerblauwe zijbalk; zonder dat logo het lichte, want dat is
        // vaker goed dan fout - en vaker goed dan een tegel met twee letters.
        logoUrl={theme?.logoDarkUrl ?? theme?.logoUrl ?? null}
        main={main}
        personal={personal}
        /*
          De zijbalk markeert op `href` en de onderbalk op `key`; deze tabel vertaalt het ene naar
          het andere. Een sleutel die hier ontbreekt, viel vroeger stil terug op "Instellingen" —
          en dan staat het verkeerde menu-item blauw zonder dat iemand het merkt. Daarom nu `null`
          voor wat er niet in staat: geen markering is beter dan de verkeerde.
        */
        activeKey={DESK_HREF[deskActiveKey ?? activeKey] ?? null}
        supportHref={support}
        wrapBrand={(brand) => (
          <TenantMenu zone="app" tone="dark">
            {brand}
          </TenantMenu>
        )}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* De mobiele kopbalk verdwijnt op desktop; daar staat de zijbalk met hetzelfde merk. */}
        {subBar ? (
          <div className="fm desk:hidden">
            <header className="fm-bar" data-testid="floor-bar">
              <button
                type="button"
                className="fm-iconbtn"
                aria-label={subBar.backLabel}
                onClick={subBar.onBack}
                data-testid="floor-back"
              >
                <ArrowLeft aria-hidden width={22} height={22} strokeWidth={2} />
              </button>
              <h1 className="fm-bar-title">{subBar.title}</h1>
            </header>
          </div>
        ) : null}
        <div className={cn('desk:hidden', subBar && 'hidden')}>
          <AppTopBar
            userName={session.profile?.displayName ?? ''}
            messages={messages}
            bell={bell}
            brandName={naam || null}
            brandLogoUrl={theme?.logoUrl ?? null}
            wrapBrand={(brand) => (
              <TenantMenu zone="app" tone="light">
                {brand}
              </TenantMenu>
            )}
          />
        </div>

        <DeskHeader
          search={<GlobalSearch zone="app" testId="desk-search" />}
          messageCount={unreadConversations.data?.available === false ? null : shownMessageCount}
          notificationCount={shownNotificationCount ?? null}
          userName={session.profile?.displayName ?? ''}
          userRole={null}
          onOpenMessages={() => void navigate({ to: '/app/gesprekken' })}
          onOpenNotifications={() => void navigate({ to: '/app/notifications' })}
          onOpenProfile={() => void navigate({ to: '/app/profile' })}
        />

        <main
          className={cn(
            // `clip` en niet `hidden`: `hidden` maakt van `main` een scrollcontainer, en dan blijft
            // een rechterkolom met `position: sticky` niet in beeld bij scrollen (VLR-1).
            'flex-1 overflow-x-clip px-4 py-4 desk:px-7 desk:pb-9 desk:pt-6',
            // §2: vaste hoogte en geen eigen scrollbalk; alleen de lijsten binnen de panelen
            // scrollen. De 68 px is de kopbalk, en die staat hier omdat de spec hem zo noemt.
            fill && 'desk:h-[calc(100vh-68px)] desk:overflow-hidden desk:py-6',
          )}
        >
          {/*
            Het inhoudsraster uit §6: één kolom tot 1100 px, daarboven een vaste rechterkolom van
            320 px. De rechterkolom staat in de DOM **vóór** de hoofdinhoud zodra er één kolom is,
            precies zoals de mockup het beschrijft — op een smal scherm hoort "wat moet ik doen"
            boven "wat is er gebeurd" te staan.
          */}
          <div
            data-testid="desk-content"
            className={cn(
              'mx-auto flex w-full max-w-md flex-col gap-element desk:max-w-[1400px]',
              // Een scherm met eigen panelen vult de hoogte en kent geen inhoudsbreedte van 1400.
              fill && 'desk:h-full desk:max-w-none',
              // Twee kolommen alléén als er een rechterkolom is. Anders staat er een lege baan van
              // 320 px naast de inhoud, en loopt het scherm niet door tot waar het mag.
              aside && 'wide:grid wide:grid-cols-[minmax(0,1fr)_320px] wide:items-start wide:gap-5',
            )}
          >
            {aside ? (
              <div className="flex flex-col gap-4 wide:order-2 wide:w-80">{aside}</div>
            ) : null}
            <div
              className={cn(
                'flex min-w-0 flex-col gap-element wide:order-1 wide:gap-5',
                fill && 'desk:h-full desk:min-h-0 desk:gap-0',
              )}
            >
              {children}
            </div>
          </div>
        </main>

        {/*
          De onderbalk hoort ín de kolom, ná `main` — niet als derde item naast de zijbalk.

          De buitenste schil is een **rij** (zijbalk | inhoud). Stond de balk daarbuiten, dan werd
          hij een derde kolom: een strook rechts van de inhoud in plaats van een balk eronder, en
          `sticky bottom-0` heeft in een rij geen betekenis. Dat was precies wat er gebeurde, en het
          liet drie specs omvallen op schermen die er verder goed uitzagen.
        */}
        {mobileFooter ? (
          <div className="fm desk:hidden" data-testid="floor-footer">
            {mobileFooter}
          </div>
        ) : (
          <div className="desk:hidden">
            <AppTabBar items={tabs} activeKey={activeKey} renderLink={renderLink} />
          </div>
        )}
      </div>
    </div>
  );
}
